import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Trash2, Users, User, Monitor, MapPin } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';

interface AvailabilityListProps {
  hostId: string;
}

export const AvailabilityList = ({ hostId }: AvailabilityListProps) => {
  const { toast } = useToast();
  
  const { data: availabilities, isLoading, refetch } = useQuery({
    queryKey: ['host-availability', hostId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('host_availability')
        .select('*')
        .eq('host_id', hostId)
        .order('start_time', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('host_availability')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Availability slot deleted successfully",
      });

      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete availability slot",
        variant: "destructive",
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  if (!availabilities?.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No availability slots found. Add some from the calendar view!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Your Availability Slots</h2>
      <div className="space-y-2">
        {availabilities.map((slot) => (
          <div
            key={slot.id}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
          >
            <div className="space-y-2">
              {slot.title && (
                <h3 className="font-medium text-lg">{slot.title}</h3>
              )}
              <div className="flex items-center gap-2">
                <p className="font-medium">
                  {format(new Date(slot.start_time), 'PPP')}
                </p>
                <div className="flex gap-2">
                  {slot.session_type === 'group' ? (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Group ({slot.max_participants} max)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Individual
                    </Badge>
                  )}
                  <Badge 
                    variant={slot.meeting_type === 'online' ? 'secondary' : 'outline'}
                    className="flex items-center gap-1"
                  >
                    {slot.meeting_type === 'online' ? (
                      <Monitor className="h-3 w-3" />
                    ) : (
                      <MapPin className="h-3 w-3" />
                    )}
                    {slot.meeting_type === 'online' ? 'Online' : 'In Person'}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {format(new Date(slot.start_time), 'p')} - {format(new Date(slot.end_time), 'p')}
              </p>
              {slot.pattern !== 'one_time' && (
                <span className="inline-block mt-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  Repeats {slot.pattern}
                </span>
              )}
              {slot.price_per_person && (
                <p className="text-sm font-medium text-gray-700">
                  ${slot.price_per_person} per person
                </p>
              )}
              {slot.notes && (
                <p className="mt-1 text-sm text-gray-500">{slot.notes}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(slot.id)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};